import * as React from 'react';
import type {ActionEvent, SuggestionWidgetPayload} from '../../types';

import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {
  BodySmall,
  SubTitleSmall,
  BodyMediumBold,
  SubTitleExtraSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Separator} from '@spaced-out/ui-design-system/lib/components/Separator';
import {Accordion} from '@spaced-out/ui-design-system/lib/components/Accordion';

import css from './suggestion-widget.css';


export type SuggestionWidgetProps = SuggestionWidgetPayload & {
  publishMessageAction?: (action: ActionEvent) => void;
};

const MAX_ELEMENTS = 3;

export const SuggestionWidget = ({
  title,
  options,
  publishMessageAction,
}: SuggestionWidgetProps) => {
  const {t} = useLabelTranslation();
  const [maxElements, setMaxElements] = React.useState(MAX_ELEMENTS);
  // Independent accordions (no group); the first opens by default.
  const [openIds, setOpenIds] = React.useState<ReadonlySet<string>>(
    () => new Set(options[0] ? [options[0].id] : []),
  );

  const toggle = (id: string, isOpen: boolean) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (isOpen) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });

  // Release the dock via the generic dock-dismiss event (same one the
  // action-card "Continue Conversation" button publishes).
  const dismiss = () =>
    publishMessageAction?.({
      product: 'graceai',
      event: 'graceai.continueconversation',
      payload: {},
    });

  return (
    <div className={css.card}>
      <header className={css.header}>
        <div className={css.headerTitle}>
          <Icon name="sparkle" size="small" />
          <SubTitleSmall>{title}</SubTitleSmall>
        </div>
        <Button
          type="ghost"
          iconLeftName="close"
          size="small"
          onClick={dismiss}
          ariaLabel={t('Close', 'Close')}
        />
      </header>

      <div className={css.list}>
        {options
          .slice(0, maxElements)
          .map(({id, title: optionTitle, details, event}) => (
            <Accordion
              key={id}
              id={id}
              header={optionTitle}
              isOpen={openIds.has(id)}
              onChange={(changedId, isOpen) => toggle(changedId, isOpen)}
              classNames={{
                wrapper: css.accordion,
                headerWrapper: css.accordionHeader,
                content: css.accordionContent,
              }}
            >
              <div className={css.body}>
                {details.map(({items, title: detailTitle}, detailIdx) => (
                  <React.Fragment key={detailTitle}>
                    {Boolean(detailIdx) && (
                      <Separator orientation="horizontal" />
                    )}
                    <div className={css.section}>
                      <SubTitleExtraSmall color="tertiary">
                        {detailTitle}
                      </SubTitleExtraSmall>
                      {items.map(({label, value}, idx) => (
                        <div
                          className={css.row}
                          key={`${detailTitle}-${label}`}
                        >
                          <BodyMediumBold
                            color="secondary"
                            className={css.rowLabel}
                          >
                            {items.length > 1 ? `${idx + 1}. ${label}` : label}
                          </BodyMediumBold>
                          {value && (
                            <BodySmall
                              color="tertiary"
                              className={css.rowValue}
                            >
                              {value}
                            </BodySmall>
                          )}
                        </div>
                      ))}
                    </div>
                  </React.Fragment>
                ))}
              </div>

              <div className={css.footer}>
                <Button
                  type="secondary"
                  isFluid
                  iconLeftName="check"
                  onClick={() => {
                    publishMessageAction?.({...event, event: event.name});
                    dismiss();
                  }}
                >
                  {t('Select_Variable', 'Select Variable')}
                </Button>
              </div>
            </Accordion>
          ))}

        {options.length > MAX_ELEMENTS ? (
          <Button
            type="tertiary"
            size="small"
            onClick={() => setMaxElements(options.length)}
            classNames={{wrapper: css.allButton}}
          >
            {`${t('See_all_variables', 'See all variables')} (${
              options.length
            })`}
          </Button>
        ) : null}
      </div>
    </div>
  );
};
